import React, { useCallback, useMemo, useRef, useState } from 'react'
import { v4 } from 'uuid'
import { l } from '@manychat/manyui'

import { useAppDispatch } from 'reduxTyped'
import { useSelectedGoalData } from 'apps/aiAutomation/components/EngagementGoalEditor/hooks/useSelectedGoalData'
import {
  GoalEditorMode,
  SubmitRef,
} from 'apps/aiAutomation/components/EngagementGoalEditor/lib/types'
import { AiAutomationAnalytics } from 'apps/aiAutomation/lib/analytics'
import { goalTypes } from 'apps/aiAutomation/lib/goals'
import { SidebarViewType } from 'apps/aiAutomation/lib/sidebar'
import { AiAutomationEngagementGoalActions } from 'apps/aiAutomation/store/engagementGoal'
import { AiAutomationLayoutActions } from 'apps/aiAutomation/store/layout'
import { alert } from 'common/core'
import { EngagementGoal, GoalStatus, GoalType } from 'shared/api/requests/aiEngagementGoal/schemas'
import { useDidMountEffect } from 'utils/commonHooks'

import { SidebarHeader } from '../SidebarHeader'

import { GrowFollowersEditor } from './components/GrowFollowersEditor'
import { LeadGenEditor } from './components/LeadGenEditor'
import { LeadQualEditor } from './components/LeadQualEditor'
import { StatusButton } from './components/StatusButton'
import { GoalEditorContext } from './lib/context'

import cm from './styles.module.css'

const getInitialEditorMode = (goal: EngagementGoal | null) => {
  if (!goal) {
    return GoalEditorMode.CREATE
  }

  return goal.status === GoalStatus.ACTIVE ? GoalEditorMode.READ : GoalEditorMode.EDIT
}

export const EngagementGoalEditor = () => {
  const dispatch = useAppDispatch()

  const { selectedGoal, selectedGoalId, goalType } = useSelectedGoalData()
  const [editorMode, setEditorMode] = useState<GoalEditorMode>(getInitialEditorMode(selectedGoal))
  const editorRef = useRef<SubmitRef<EngagementGoal>>(null)
  const goalId = useMemo(() => selectedGoalId ?? v4(), [selectedGoalId])

  const save = useCallback(
    async (goal: EngagementGoal, saveAndPublish: boolean = false) => {
      if (selectedGoalId) {
        await dispatch(AiAutomationEngagementGoalActions.updateFx({ ...goal, id: selectedGoalId }))

        if (saveAndPublish) {
          alert(l.getString(goalTypes[goal.type].successPublishingAlert), 'success', {
            icon: 'CheckCircle',
          })
          AiAutomationAnalytics.logGoalStatusChanged({ id: selectedGoalId, is_published: true })
        } else {
          AiAutomationAnalytics.logGoalUpdated({ id: selectedGoalId })
          alert(l.translate('Your changes were saved'), 'success', {
            icon: 'CheckCircle',
          })
        }
        return
      }

      const result = await dispatch(AiAutomationEngagementGoalActions.createFx(goal)).unwrap()

      dispatch(
        AiAutomationLayoutActions.viewPayloadUpdated({
          view: SidebarViewType.ENGAGEMENT_GOAL_EDITOR,
          payload: { goalId: result.id },
        }),
      )

      AiAutomationAnalytics.logGoalCreated({ id: result.id })

      setEditorMode(GoalEditorMode.EDIT)
    },
    [dispatch, selectedGoalId],
  )

  useDidMountEffect(() => {
    AiAutomationAnalytics.logGoalEditorOpened({ id: goalId, goal_type: goalType })
  })

  const goalEditorContextValues = useMemo(
    () => ({
      mode: editorMode,
      goalId,
      goal: selectedGoal,
      save,
      changeMode: setEditorMode,
      editorRef,
    }),
    [editorMode, goalId, save, selectedGoal, editorRef],
  )

  return (
    <GoalEditorContext.Provider value={goalEditorContextValues}>
      <section className={cm.wrapper}>
        <SidebarHeader
          title={l.getString(goalTypes[goalType].name)}
          button={
            selectedGoal && editorMode !== GoalEditorMode.CREATE ? (
              <StatusButton goal={selectedGoal} />
            ) : null
          }
        />

        {goalType === GoalType.LEAD_GEN && <LeadGenEditor ref={editorRef} />}

        {goalType === GoalType.GROW_FOLLOWERS && <GrowFollowersEditor ref={editorRef} />}

        {goalType === GoalType.LEAD_QUAL && <LeadQualEditor passedRef={editorRef} />}
      </section>
    </GoalEditorContext.Provider>
  )
}
